import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Church, Search, MapPin, Users, Loader2, ChevronRight, Clock } from "lucide-react";
import { getParishes, requestMembership, getMyPendingParishRequests } from "@/lib/api";
import { toast } from "sonner";
import { MembershipBanner } from "@/components/MembershipModal";
import { useSEO } from "@/hooks/useSEO";

export default function ParishesPage({ currentUser, isPublic = false }) {
  const navigate = useNavigate();
  const [parishes, setParishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestingParish, setRequestingParish] = useState(null);

  // SEO
  useSEO({
    title: "Find a Parish",
    description: "Browse and join Catholic parishes in your area. Find Mass times, events, and connect with your local Catholic community.",
    path: "/parishes"
  });

  useEffect(() => {
    loadParishes();
    if (!isPublic) {
      loadPendingRequests();
    }
  }, [isPublic]);

  const loadParishes = async () => {
    setLoading(true);
    try {
      const res = await getParishes();
      // Handle both array and {parishes: []} response formats
      const data = res.data;
      setParishes(Array.isArray(data) ? data : (data.parishes || []));
    } catch (err) {
      console.error("Failed to load parishes:", err);
      toast.error("Failed to load parishes");
    }
    setLoading(false);
  };

  const loadPendingRequests = async () => {
    try {
      const res = await getMyPendingParishRequests();
      setPendingRequests(res.data.pending_requests || []);
    } catch (err) {
      console.error("Failed to load pending requests:", err);
    }
  };

  const handleRequestJoin = async (e, parishId) => {
    e.stopPropagation(); // Prevent card click navigation
    setRequestingParish(parishId);
    try {
      await requestMembership(parishId);
      toast.success("Request sent! Awaiting parish admin approval.");
      setPendingRequests([...pendingRequests, parishId]);
    } catch (err) {
      const message = err.response?.data?.detail || "Request failed";
      if (message.includes("already pending")) {
        toast.info("You already have a pending request for this parish");
        setPendingRequests([...pendingRequests, parishId]);
      } else if (message.includes("already a member")) {
        toast.info("You're already a member of this parish");
      } else {
        toast.error(message);
      }
    }
    setRequestingParish(null);
  };

  const filteredParishes = parishes.filter(parish => {
    const query = searchQuery.toLowerCase();
    return (
      parish.name?.toLowerCase().includes(query) ||
      parish.address?.toLowerCase().includes(query) ||
      parish.location?.toLowerCase().includes(query) ||
      parish.diocese?.toLowerCase().includes(query) ||
      parish.diocese_name?.toLowerCase().includes(query) ||
      parish.city?.toLowerCase().includes(query)
    );
  });

  // Check if user is member of a parish
  const userParishIds = currentUser?.parish_ids || [];
  if (currentUser?.parish_id && !userParishIds.includes(currentUser.parish_id)) {
    userParishIds.push(currentUser.parish_id);
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2] p-4 md:p-6">
      
      <div className="max-w-4xl mx-auto">
        {/* Public Access Banner */}
        {isPublic && (
          <MembershipBanner context="parish" className="mb-6" />
        )}
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]">Find a Parish</h1>
          <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
            Browse and join Catholic parishes in your area
          </p>
        </div>

        {/* Search */}
        <Card className="bg-white border-[#E5E0D5] mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, location, or diocese..."
                className="pl-10 border-[#E5E0D5] font-['Nunito']"
                data-testid="parish-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
          </div>
        ) : filteredParishes.length === 0 ? (
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="py-12 text-center">
              <Church className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">
                {searchQuery ? "No parishes found" : "No parishes available"}
              </h3>
              <p className="text-sm text-[#6B665E] font-['Nunito']">
                {searchQuery ? "Try a different search term" : "Check back later for available parishes"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredParishes.map((parish) => {
              const isMember = userParishIds.includes(parish.parish_id);
              const isPending = pendingRequests.includes(parish.parish_id);
              const isRequesting = requestingParish === parish.parish_id;
              
              return (
                <Card 
                  key={parish.parish_id}
                  className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/parish/${parish.parish_id}`)}
                  data-testid={`parish-card-${parish.parish_id}`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center flex-shrink-0">
                        <Church className="w-5 h-5 sm:w-7 sm:h-7 text-[var(--liturgical-primary)]" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-['Crimson_Text'] font-semibold text-[#2D2A26] text-base sm:text-lg truncate">
                            {parish.name}
                          </h3>
                          {isMember && (
                            <Badge className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] border-[var(--liturgical-primary)] text-[10px] sm:text-xs flex-shrink-0">
                              Member
                            </Badge>
                          )}
                          {isPending && !isMember && (
                            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-[10px] sm:text-xs flex-shrink-0">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </div>
                        
                        {parish.address && (
                          <p className="text-xs sm:text-sm text-[#6B665E] font-['Nunito'] flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{parish.address}</span>
                          </p>
                        )}
                        
                        <div className="flex items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2 flex-wrap">
                          {parish.diocese && (
                            <span className="text-[10px] sm:text-xs text-[#6B665E] font-['Nunito'] truncate max-w-[150px] sm:max-w-none">
                              {parish.diocese}
                            </span>
                          )}
                          {parish.member_count > 0 && (
                            <span className="text-[10px] sm:text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {parish.member_count}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Request to Join button - show if not a member and no pending request */}
                      {!isMember && !isPending && (
                        <Button
                          size="sm"
                          onClick={(e) => handleRequestJoin(e, parish.parish_id)}
                          disabled={isRequesting}
                          className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-[10px] sm:text-xs flex-shrink-0 h-7 sm:h-8 px-2 sm:px-3"
                          data-testid={`join-parish-${parish.parish_id}`}
                        >
                          {isRequesting ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <span className="hidden sm:inline">Request to Join</span>
                              <span className="sm:hidden">Join</span>
                            </>
                          )}
                        </Button>
                      )}
                      
                      {(isMember || isPending) && (
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#6B665E] flex-shrink-0" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            Can't find your parish?{" "}
            <Button 
              variant="link" 
              className="text-[var(--liturgical-primary)] p-0 h-auto font-['Nunito']"
              onClick={() => navigate("/sacraments")}
            >
              Use the Sacrament Finder
            </Button>
            {" "}to search by Mass times and location.
          </p>
        </div>
      </div>
    </div>
  );
}
